import type { MobxZodPlugin } from '@monoid-dev/mobx-zod-form';
import {
  init,
  Replay,
  BrowserTracing,
  getCurrentHub,
  reactRouterV5Instrumentation,
} from '@sentry/react';
import { getConfig } from '@utils/config';
import type { History } from 'history';
import { BadResponse, StatusNotHandled, TyrannMiddleware } from 'tyrann-io';

export const initTracing = (history: History) => {
  init({
    dsn: SENTRY_DSN,
    replaysSessionSampleRate: process.env.NODE_ENV === 'production' ? 1.0 : 0.1,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      new Replay({
        maskAllText: false,
      }),
      new BrowserTracing({
        tracingOrigins: ['localhost', new URL(getConfig().serverUrl).hostname],
        routingInstrumentation: reactRouterV5Instrumentation(history),
      }),
    ],
    release: `${import.meta.env.VITE_APP_STAGE}:${GIT_HASH}`,
    environment: import.meta.env.VITE_APP_STAGE ?? 'development',
    tracesSampleRate: 1.0,
    normalizeDepth: 5,
    enabled: !import.meta.env.DEV || process.env.VITE_APP_CI === 'true',
  });
};

const getOrCreateTransaction = () => {
  const tx =
    getCurrentHub().getScope()?.getTransaction() ??
    getCurrentHub().startTransaction({ name: location.pathname });
  getCurrentHub().configureScope((scope) => scope.setSpan(tx));
  return tx;
};

export const tyrannSentryMiddleware: TyrannMiddleware = async (callAction, _localOptions, next) => {
  const tx = getOrCreateTransaction();

  const span = tx.startChild({
    op: 'tyrann-request',
    description: `${callAction.method} ${callAction.name}`,
  });

  try {
    const result = await next();
    span.setStatus('ok');
    return result;
  } catch (e) {
    if (e instanceof StatusNotHandled) {
      span.setHttpStatus(e.status);
    } else if (e instanceof BadResponse) {
      e.response?.status && span.setHttpStatus(e.response?.status);
      span.setStatus('internal_error');
    } else {
      span.setStatus('unknown_error');
    }

    throw e;
  } finally {
    span.finish();
  }
};

export const mobxFormMetricsPlugin = (): MobxZodPlugin => {
  const stats = {
    mounted: 0,
    focused: 0,
    firstSubmit: 0,
    firstErrorClear: 0,
    get focusedToFirstSubmit() {
      return this.firstSubmit > 0 ? this.firstSubmit - this.focused : undefined;
    },
    get focusedToFirstClearError() {
      return this.firstErrorClear > 0 ? this.firstErrorClear - this.focused : undefined;
    },
    submitCount: 0,
  };

  const updateMeasurements = () => {
    if (stats.focusedToFirstSubmit) {
      getOrCreateTransaction().setMeasurement(
        'form.focusedToFirstSubmit',
        stats.focusedToFirstSubmit,
        'millisecond',
      );
    }
    if (stats.focusedToFirstClearError) {
      getOrCreateTransaction().setMeasurement(
        'form.focusedToFirstClearError',
        stats.focusedToFirstClearError,
        'millisecond',
      );
    }
    if (stats.submitCount) {
      getOrCreateTransaction().setMeasurement('form.submitCount', stats.submitCount, 'none');
    }

    console.info('mobxFormMetricsPlugin stats', JSON.stringify(stats, undefined, 2));
  };

  const onFocus = () => {
    if (stats.focused === 0) {
      stats.focused = performance.now();
    }
  };

  return {
    name: 'mobxFormMetricsPlugin',
    onStart: (form) => {
      stats.mounted = performance.now();
      form.element?.addEventListener('focusin', onFocus);
    },
    onEnd: (form) => {
      form.element?.removeEventListener('focusin', onFocus);
    },
    onAfterValidate(form) {
      if (stats.firstErrorClear === 0 && form.parsed.success && stats.focused) {
        stats.firstErrorClear = performance.now();
        updateMeasurements();
      }
    },
    onBeforeSubmit() {
      if (stats.firstSubmit === 0 && stats.focused) {
        stats.firstSubmit = performance.now();
      }
    },
    onAfterSubmit() {
      if (stats.focused) {
        stats.submitCount++;
        updateMeasurements();
      }
    },
  };
};
